import React from 'react';

export interface NavigationState {
  showPrevious: boolean;
  showNext: boolean;
  previousStreamId: string | null;
  nextStreamId: string | null;
}

interface StreamMetadata {
  streamId: string;
}

/**
 * Shared hook for AI navigation logic
 * Handles navigation state calculation for AI streams
 */
export const useAINavigation = (
  aiStream: string | undefined,
  streams: StreamMetadata[] | undefined,
): NavigationState => {
  return React.useMemo(() => {
    if (!streams || !streams.length || !aiStream || streams.length <= 1) {
      return { showPrevious: false, showNext: false, previousStreamId: null, nextStreamId: null };
    }

    const currentIndex = streams.findIndex(stream => stream.streamId === aiStream);
    if (currentIndex === -1) {
      return { showPrevious: false, showNext: false, previousStreamId: null, nextStreamId: null };
    }

    const isNewest = currentIndex === 0;
    const isOldest = currentIndex === streams.length - 1;

    return {
      showPrevious: !isOldest, // Can go to older (higher index)
      showNext: !isNewest, // Can go to newer (lower index)
      previousStreamId: !isOldest ? streams[currentIndex + 1].streamId : null, // Older response
      nextStreamId: !isNewest ? streams[currentIndex - 1].streamId : null, // Newer response
    };
  }, [streams, aiStream]);
};
